import MathManager from '@/utils/math-manager.utils'
import DateManager from '@/utils/date-manager.utils'

import {
  FIFTEEN_DAYS,
  INTERVAL,
  ONE_MONTH,
  SEVEN_DAYS,
  SPECIFIC_DATE,
  TODAY,
  YESTERDAY
} from '@/enums/date-picker.enum'

import { CHARGES_ENUM, CURRENCY_SYMBOL, STEPS } from './charge.enum'

class ChargesManager {
  data
  // The visualization type (ticket, tpv, sales)
  chargesType
  queryString

  constructor (data, chargesType, queryString) {
    this.data = data
    this.chargesType = chargesType
    this.queryString = queryString
  }

  // Adapter that converts the time_series into the the graph format
  adapter ({ data, filter, period }) {
    const defaultFormatConfig = {
      format: MathManager.formatCurrency,
      currencySymbol: CURRENCY_SYMBOL.R$,
      currencyDisplay: false,
      steps: STEPS.DECIMAL
    }

    const defaultConfig = {
      yAxisOptions: defaultFormatConfig,
      tooltipConfig: defaultFormatConfig,
      xLabels: this.xlabelsDefault({ data, filter, period })
    }

    const config = {
      [CHARGES_ENUM.TPV]: {
        ...defaultConfig,
        data: this.tpvAdapter(data),
        title: 'total-traded-volume'
      },
      [CHARGES_ENUM.SALES]: {
        ...defaultConfig,
        data: this.salesAdapter(data),
        title: 'charges-amount',
        yAxisOptions: {
          steps: STEPS.ROUND
        },
        tooltipConfig: {}
      },
      [CHARGES_ENUM.TICKET]: {
        ...defaultConfig,
        data: this.ticketsAdapter(data),
        title: 'ticket'
      }
    }
    return config[this.chargesType]
  }

  isDateDiffTwoDays (startDate, endDate) {
    return (
      DateManager.diffInDate(startDate, endDate) >= 0 &&
      DateManager.diffInDate(startDate, endDate) <= 2
    )
  }

  // The X Series of the graph, depending on the filter, it shows a different format
  // TODAY YESTERDAY SPECIFIC_DATE => HOURS
  // SEVEN_DAYS FIFTEEN_DAYS ONE_MONTH INTERVAL => DATE
  xlabelsDefault ({ data, filter, period }) {
    const { start: startDate, end: endDate } = period

    const dateFilters = [
      SEVEN_DAYS.id,
      FIFTEEN_DAYS.id,
      ONE_MONTH.id,
      INTERVAL.id
    ].map(el => el.toLowerCase())

    if (filter === INTERVAL.id && DateManager.isSameDay(startDate, endDate)) {
      return this.periodAdapter(data, DateManager.formatToInputHours)
    }

    if (
      filter.toLowerCase() === INTERVAL.id &&
      this.isDateDiffTwoDays(startDate, endDate)
    ) {
      return this.periodAdapter(data, DateManager.formatToDateTime)
    }

    if (dateFilters.includes(filter.toLowerCase())) {
      return this.periodAdapter(data, DateManager.formatToInputDate)
    }

    const hoursFilter = [TODAY.id, YESTERDAY.id, SPECIFIC_DATE.id].map(el =>
      el.toLowerCase()
    )

    if (hoursFilter.includes(filter.toLowerCase())) {
      return this.periodAdapter(data, DateManager.formatToInputHours)
    }
  }

  // The tpv view format
  tpvAdapter (data) {
    return data.map(item => MathManager.convertCentsToBRL(item.tpv))
  }

  // The sales view format
  salesAdapter (data) {
    return data.map(item => item.quantity)
  }

  // The ticket view format
  ticketsAdapter (data) {
    const result = data.map(item => {
      if (item.average_ticket_authorized)
        return MathManager.convertCentsToBRL(item.average_ticket_authorized)
      return MathManager.convertCentsToBRL(0)
    })

    return result
  }

  // Formats the data based on the passed formatter
  periodAdapter (data, formatter) {
    return data.map(item => formatter(item.name))
  }

  // Execute all the methods
  get () {
    const {
      date_filters: dateFilter,
      period_type: periodType,
      created_since: start,
      created_until: end
    } = this.queryString

    const filter = dateFilter ?? periodType

    return this.adapter({
      data: this.data,
      filter: filter?.toLowerCase() || TODAY.id,
      period: { start, end }
    })
  }
}

export default ChargesManager
